import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProfileCard from "@/components/ProfileCard";
import { Heart, MessageCircle, Eye, Crown, UserCog, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ shortlist: 0, interests: 0, messages: 0 });
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [sh, intR, ms, rv] = await Promise.all([
                    client.get("/shortlist"),
                    client.get("/interests?box=received"),
                    client.get("/conversations"),
                    client.get("/recently-viewed"),
                ]);
                setStats({ shortlist: sh.data.length, interests: intR.data.length, messages: ms.data.length });
                setRecent(rv.data);
            } catch (e) {
                toast.error("Failed to load dashboard");
            }
        };
        load();
    }, []);

    const isPremium = user?.premium_until && new Date(user.premium_until) > new Date();

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12">
            <div className="flex items-start justify-between flex-wrap gap-6 mb-12">
                <div>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">My Dashboard</div>
                    <h1 className="mt-2 font-serif text-5xl text-navy tracking-tighter">Peace be with you, <span className="italic">{user?.name?.split(" ")[0]}</span></h1>
                    <p className="mt-2 text-navy-500 font-sans">Your journey toward sacred matrimony continues.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Link to="/profile/setup" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] border border-navy-200 text-navy rounded-lg px-5 py-3 hover:border-navy hover:bg-navy hover:text-white transition-all">
                        <UserCog className="h-4 w-4" /> Edit Profile
                    </Link>
                    {!isPremium && (
                        <Link to="/premium" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] bg-gold text-navy rounded-lg px-5 py-3 hover:bg-gold-600 hover:text-white transition-all shadow-md shadow-gold/20">
                            <Crown className="h-4 w-4" /> Upgrade
                        </Link>
                    )}
                </div>
            </div>

            {/* Profile status banner */}
            {user?.profile_status === "pending" && (
                <div className="mb-10 bg-linen border border-gold-200 rounded-2xl p-6 flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-gold-100 flex items-center justify-center">
                        <BadgeCheck className="h-5 w-5 text-gold-700" />
                    </div>
                    <div>
                        <div className="font-serif text-xl text-navy">Your profile awaits approval</div>
                        <div className="text-sm text-navy-500 font-sans">Complete your profile to be discoverable. Our team will review and approve within 24 hours.</div>
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-6 mb-12">
                <StatCard icon={<Heart />} label="Shortlisted" value={stats.shortlist} href="/shortlist" />
                <StatCard icon={<Eye />} label="Interests Received" value={stats.interests} href="/notifications" />
                <StatCard icon={<MessageCircle />} label="Conversations" value={stats.messages} href="/messages" />
            </div>

            {/* Recently Viewed */}
            <div>
                <div className="flex items-end justify-between mb-6">
                    <h2 className="font-serif text-3xl text-navy">Recently Viewed</h2>
                    <Link to="/search" className="text-xs uppercase tracking-[0.18em] text-navy-500 hover:text-gold-600 font-sans">Browse more →</Link>
                </div>
                {recent.length === 0 ? (
                    <div className="bg-linen rounded-2xl p-10 text-center text-navy-500 font-sans">
                        You haven&rsquo;t viewed any profiles yet. <Link to="/search" className="text-gold-700 underline">Start browsing</Link>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {recent.slice(0, 4).map((p) => <ProfileCard key={p.user_id} profile={p} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, href }) {
    return (
        <Link to={href} className="bg-white border border-navy-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-linen text-gold-700 flex items-center justify-center">{icon}</div>
            <div>
                <div className="font-serif text-3xl text-navy leading-none">{value}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-navy-400 mt-1">{label}</div>
            </div>
        </Link>
    );
}
